import { ChevronUp, Zap } from "lucide-react"
import { useTranslation } from "react-i18next"
import WorkflowList from "./WorkflowList"
import { useVisibleWorkflows } from "@/hooks/useVisibleWorkflows"

/** Prominent collapsed representation of the automations widget — an icon with
 * a blue count badge. Reused as the floating trigger (no map) and inline (map). */
export const AutomationsToggleButton = ({
  onClick,
}: {
  onClick: () => void
}) => {
  const { t } = useTranslation()
  const { count } = useVisibleWorkflows()
  return (
    <button
      onClick={onClick}
      aria-label={t("ai.automations.title")}
      title={t("ai.automations.title")}
      className="relative grid h-11 w-11 place-items-center rounded-full border border-border bg-card text-foreground shadow-md transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <Zap className="h-5 w-5" />
      {count > 0 && (
        <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-lg bg-base-primary p-1 text-[10px] font-semibold text-white dark:bg-blue-500 dark:text-black">
          {count}
        </span>
      )}
    </button>
  )
}

type Props = {
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
}

const WorkflowWidget = ({ expanded, onExpandedChange }: Props) => {
  const { t } = useTranslation()
  const { count } = useVisibleWorkflows()

  if (!expanded) {
    return (
      // Inline collapsed state only renders alongside a map — align the icon to
      // the right edge of the sidebar panel.
      <div className="flex w-full shrink-0 justify-end p-3 pb-0">
        <AutomationsToggleButton onClick={() => onExpandedChange(true)} />
      </div>
    )
  }

  return (
    <div className="flex w-full shrink-0 flex-col gap-3 bg-background p-3 pb-0">
      <div className="flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-md">
        <div className="flex items-center justify-between px-4 py-3">
          <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
            <Zap className="h-4 w-4 text-foreground" />
            {t("ai.automations.title")}
            {count > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-lg bg-base-primary px-1 text-[10px] font-semibold text-white dark:bg-blue-500 dark:text-black">
                {count}
              </span>
            )}
          </h2>
          <button
            onClick={() => onExpandedChange(false)}
            className="text-foreground transition-colors"
            aria-label={t("ai.automations.collapse")}
          >
            <ChevronUp className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="max-h-[40vh] overflow-y-auto border-t border-gray-100 dark:border-gray-800">
          <WorkflowList showHeader={false} />
        </div>
      </div>
    </div>
  )
}

export default WorkflowWidget
